/**
 * Iter 193 — Shared CAPA Panel component.
 *
 * Extracted from the inline panel in Finance.jsx. Used by:
 *   · Finance.jsx  →  "Talepler / Bildirimler" → GMP CAPA tab (backward compat)
 *   · GMP.jsx      →  GMP Portal → CAPA tab
 *
 * Data source: `/requests` (single source of truth, `capa_auto || capa_source`).
 * Manual CAPAs opened from QMS modules (Deviation/Complaint/OOS) carry
 * `capa_auto=false` + `capa_source ∈ {deviation, complaint, oos}` +
 * `capa_source_ref`. Auto CAPAs (existing GMP triggers) carry
 * `capa_auto=true` + `capa_source ∈ {cleaning, temp_humidity, ...}`.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Btn, Badge } from "@/components/UI";
import { openPdfPreview } from "@/lib/pdfPreview";
import { FileText, Plus } from "lucide-react";
import ManualCapaModal from "@/components/ManualCapaModal";
import GmpEvidencePanel from "@/components/GmpEvidencePanel";

const SOURCE_LABELS = {
  cleaning: "🧼 Günlük Temizlik",
  temp_humidity: "🌡 Sıcaklık/Nem",
  qc_fail: "🧪 QC Fail",
  general: "📋 Genel",
  deviation: "⚠ Sapma",
  complaint: "📞 Şikayet",
  oos: "🧪 OOS",
};
const sourceLabel = (k) => SOURCE_LABELS[k] || (k || "GMP");

export function CAPAPanel({ testidPrefix = "capa", showBothTypes = true }) {
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [capaMonth, setCapaMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [filter, setFilter] = useState("all"); // all | auto | manual
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    const r = await api.get("/requests");
    const capas = (r || []).filter(x => x.capa_auto || x.capa_source);
    capas.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    setRows(capas);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (r, status) => {
    try {
      await api.put(`/requests/${r.id}`, { ...r, status });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Durum güncellenemedi"); }
  };
  const del = async (id) => {
    if (window.confirm("CAPA kaydı silinsin/arşivlensin mi?")) {
      try { await api.del(`/requests/${id}`); load(); } catch (e) { alert(e.response?.data?.detail || "Silinemedi"); }
    }
  };
  const toggle = (id) => { const s = new Set(expanded); s.has(id) ? s.delete(id) : s.add(id); setExpanded(s); };

  const filtered = rows.filter(r => {
    if (filter === "auto") return !!r.capa_auto;
    if (filter === "manual") return !r.capa_auto;
    return true;
  });

  const dlPdf = async () => {
    try {
      const token = localStorage.getItem("erp_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/gmp/capa-report-pdf?month=${capaMonth}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { alert(`PDF alınamadı (${r.status})`); return; }
      const blob = await r.blob();
      openPdfPreview(blob, `GMP-CAPA-${capaMonth}.pdf`);
    } catch (e) { alert(`PDF hatası: ${e.message}`); }
  };

  return (
    <div data-testid={`${testidPrefix}-panel`}>
      <div className="bg-amber-50/70 border border-amber-200 rounded-lg p-3 mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 text-xs text-amber-900">
          <b>🛡 CAPA (Düzeltici / Önleyici Faaliyet):</b> ISO 22716 & GMP uyumu için — otomatik GMP tetiklemeleri ve
          Sapma / Şikayet / OOS modüllerinden manuel açılan kayıtlar tek yerde. Kapatma için her kayıtta
          <b> Kök Neden</b> + <b>Önleyici Aksiyon</b> zorunludur.
        </div>
        {showBothTypes && (
          <select value={filter} onChange={e => setFilter(e.target.value)}
            className="text-xs px-2 py-1 border border-amber-300 rounded bg-white" data-testid={`${testidPrefix}-filter`}>
            <option value="all">Tümü ({rows.length})</option>
            <option value="auto">Otomatik ({rows.filter(r => r.capa_auto).length})</option>
            <option value="manual">Manuel ({rows.filter(r => !r.capa_auto).length})</option>
          </select>
        )}
        <input type="month" value={capaMonth} onChange={e => setCapaMonth(e.target.value)}
          className="text-xs px-2 py-1 border border-amber-300 rounded bg-white" data-testid={`${testidPrefix}-month`}/>
        <Btn size="sm" onClick={dlPdf} data-testid={`${testidPrefix}-report-pdf`}>📄 Aylık CAPA Raporu PDF</Btn>
        <Btn size="sm" onClick={() => setCreateOpen(true)} data-testid={`${testidPrefix}-manual-create`}>
          <Plus size={12}/> CAPA Oluştur
        </Btn>
      </div>

      <ManualCapaModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load}/>

      <div className="bg-white border border-amber-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-amber-100 text-amber-900 text-xs">
            <tr>
              <th className="px-3 py-2 w-6"></th>
              <th className="text-left px-3 py-2">No</th>
              <th className="text-left px-3 py-2">Uygunsuzluk</th>
              <th className="text-left px-3 py-2">Kaynak</th>
              <th className="text-left px-3 py-2">Tip</th>
              <th className="text-left px-3 py-2">Öncelik</th>
              <th className="text-left px-3 py-2">Durum</th>
              <th className="text-left px-3 py-2">Tarih</th>
              <th className="text-left px-3 py-2">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => {
              const isOpen = expanded.has(r.id);
              const ref = r.capa_source_ref || null;
              return (
                <React.Fragment key={r.id}>
                  <tr className="border-t border-amber-100 hover:bg-amber-50/40 cursor-pointer"
                      onClick={() => toggle(r.id)} data-testid={`${testidPrefix}-row-${r.id}`}>
                    <td className="px-3 py-2 text-slate-500">{isOpen ? "▾" : "▸"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.number}</td>
                    <td className="px-3 py-2 font-semibold text-slate-800">{r.title}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className="bg-amber-100 border border-amber-300 text-amber-800 px-2 py-0.5 rounded font-mono">
                        {sourceLabel(r.capa_source)}
                      </span>
                      {ref?.number && <div className="text-[10px] text-slate-500 mt-0.5">↳ {ref.number}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.capa_auto
                        ? <span className="bg-rose-100 border border-rose-300 text-rose-800 px-1.5 py-0.5 rounded">Otomatik</span>
                        : <span className="bg-sky-100 border border-sky-300 text-sky-800 px-1.5 py-0.5 rounded">Manuel</span>}
                    </td>
                    <td className="px-3 py-2">
                      <Badge level={r.priority === "critical" ? "missing" : r.priority === "high" ? "critical"
                                    : r.priority === "normal" ? "attention" : "inactive"}>{r.priority}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      <Badge level={r.status === "completed" ? "ok" : r.status === "cancelled" ? "inactive" : "attention"}>{r.status}</Badge>
                    </td>
                    <td className="px-3 py-2 text-xs">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1 flex-wrap">
                        {r.status === "open" && <Btn size="sm" variant="secondary" onClick={() => setStatus(r, "in_progress")} data-testid={`${testidPrefix}-start-${r.id}`}>Başla</Btn>}
                        {r.status !== "completed" && <Btn size="sm" onClick={() => setStatus(r, "completed")} data-testid={`${testidPrefix}-complete-${r.id}`}>Kapat</Btn>}
                        {/* Iter 202 §2 — CAPA controlled PDF (preview-first via existing PdfPreviewDialog). */}
                        <Btn size="sm" variant="ghost" onClick={async () => {
                          try {
                            const blob = await api.getBlob(`/gmp/capa/${r.id}/pdf`);
                            openPdfPreview(blob, `CAPA_${r.number || r.id}.pdf`);
                          } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
                        }} data-testid={`${testidPrefix}-pdf-${r.id}`} title="CAPA PDF Önizleme"><FileText size={12}/></Btn>
                        <Btn size="sm" variant="danger" onClick={() => del(r.id)} data-testid={`${testidPrefix}-delete-${r.id}`}>Sil</Btn>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-amber-50/50 border-t border-amber-100" data-testid={`${testidPrefix}-detail-${r.id}`}>
                      <td colSpan={9} className="px-6 py-4">
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>
                            <div className="text-slate-500 uppercase font-semibold mb-1">Uygunsuzluk Detayı</div>
                            <div className="bg-white border border-amber-200 rounded p-3 text-slate-700 whitespace-pre-wrap min-h-[80px]">
                              {r.description || <span className="italic text-slate-400">Açıklama yok.</span>}
                            </div>
                          </div>
                          <div className="space-y-1.5">
                            <div><span className="text-slate-500">Kaynak Modül:</span> <span className="font-semibold">{sourceLabel(r.capa_source)}</span></div>
                            {ref && (
                              <div className="bg-white border border-slate-200 rounded p-2">
                                <div className="text-[10px] uppercase text-slate-500 font-semibold">Bağlı Kayıt</div>
                                <div className="font-mono text-[11px]">{ref.number}</div>
                                <div className="text-[11px] text-slate-600 truncate">{ref.title || "—"}</div>
                              </div>
                            )}
                            <div><span className="text-slate-500">Atanan:</span> <span className="font-semibold">{r.assigned_to || "Kalite / GMP"}</span></div>
                            <div><span className="text-slate-500">Oluşturma:</span> <span className="font-mono">{(r.created_at || "").slice(0, 19).replace("T", " ")}</span></div>
                            {r.completed_at && <div><span className="text-slate-500">Kapatma:</span> <span className="font-mono">{r.completed_at.slice(0, 19).replace("T", " ")}</span></div>}
                          </div>
                          <div className="col-span-2 grid grid-cols-2 gap-3 mt-2">
                            <div>
                              <label className="text-[10px] uppercase text-amber-800 font-bold tracking-wider mb-1 block">🎯 Kök Neden <span className="text-red-600">*</span></label>
                              <textarea rows={3} placeholder="5-Neden analizi ile kök nedeni yazın..."
                                defaultValue={r.capa_root_cause || ""}
                                onBlur={async (e) => {
                                  const v = e.target.value.trim();
                                  if (v === (r.capa_root_cause || "")) return;
                                  try { await api.put(`/requests/${r.id}`, { ...r, capa_root_cause: v }); load(); }
                                  catch (er) { alert(er.response?.data?.detail || "Kaydedilemedi"); }
                                }}
                                className="w-full px-2 py-2 text-xs border border-amber-300 rounded bg-white text-slate-800"
                                data-testid={`${testidPrefix}-root-${r.id}`}/>
                            </div>
                            <div>
                              <label className="text-[10px] uppercase text-emerald-800 font-bold tracking-wider mb-1 block">🛡 Önleyici Aksiyon <span className="text-red-600">*</span></label>
                              <textarea rows={3} placeholder="Tekrarını önlemek için alınan/alınacak aksiyon..."
                                defaultValue={r.capa_preventive_action || ""}
                                onBlur={async (e) => {
                                  const v = e.target.value.trim();
                                  if (v === (r.capa_preventive_action || "")) return;
                                  try { await api.put(`/requests/${r.id}`, { ...r, capa_preventive_action: v }); load(); }
                                  catch (er) { alert(er.response?.data?.detail || "Kaydedilemedi"); }
                                }}
                                className="w-full px-2 py-2 text-xs border border-emerald-300 rounded bg-white text-slate-800"
                                data-testid={`${testidPrefix}-prev-${r.id}`}/>
                            </div>
                            <div className="col-span-2 bg-white border border-amber-200 rounded p-2 text-amber-900 text-[11px]">
                              <b>ℹ CAPA (Düzeltici / Önleyici Faaliyet):</b> ISO 22716 & GMP uyumu için her iki alan da zorunludur. Boş bırakılırsa "Kapat" butonu reddeder.
                            </div>
                            <div className="col-span-2">
                              <GmpEvidencePanel module="capa" recordId={r.id}/>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-slate-400 text-sm">✓ Aktif CAPA kaydı yok.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CAPAPanel;
